import axios from 'axios';
import type {
  LineupViewModel,
  ShowdownLineupViewModel,
  FootballLineupViewModel,
  LineupAnalyticsViewModel,
  ContestViewModel,
  BasketballLineupViewModel,
  FootballProjectionSourceHealth,
  ContestType} from '../types/ContestViewModel';
import type { BacktestRun, BacktestSimulationResult } from '../types/BacktestTypes';
import type { PropCorrelation } from '../types/CorrelationTypes';
import type { LineupResultRow } from '../types/LineupResultTypes';
import type { DkContestUploadResult, ContestPerformanceSummary, ContestLedgerRow, PayoutFitLatest, HistoryDimension } from '../types/ContestResultsTypes';
import type { EntriesResponse, MarkEnteredRequest } from '../types/BaseballDfsTypes';
import type {
  BasketballEntriesResponse,
  BasketballMarkEnteredRequest,
  FootballEntriesResponse,
  FootballMarkEnteredRequest,
  MarkEnteredResponse,
  DfsSport,
  DfsEnteredCountsResponse,
} from '../types/DfsEntriesTypes';
import type {
  LineupSport, LineupFilterState, LineupPage, LineupsByIds, LineupBands, PlayerOption, SelectionResponse,
} from '../types/lineupQuery';
import { LINEUP_BYIDS_MAX } from '../types/lineupQuery';
import type { OutlierFlagResponse } from '../types/OutlierFlagTypes';

// Determine API URL based on environment
const getApiUrl = () => {
  // In production, the React app and API are on the same domain
  if (window.location.hostname === 'fs.stevegala.com') {
    return 'https://fs.stevegala.com';
  }
  // Local development
  return 'http://localhost:54321';
};

const apiClient = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
});

// Request interceptor: attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('fs_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401 with token refresh
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(prom => {
    if (token) prom.resolve(token);
    else prom.reject(error);
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('fs_refresh_token');
        if (!refreshToken) throw new Error('No refresh token');

        const response = await axios.post(`${getApiUrl()}/api/auth/refresh`, {
          refreshToken,
        });

        const { accessToken: newAccess, refreshToken: newRefresh } = response.data;
        localStorage.setItem('fs_access_token', newAccess);
        localStorage.setItem('fs_refresh_token', newRefresh);

        processQueue(null, newAccess);
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('fs_access_token');
        localStorage.removeItem('fs_refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// In-flight GET dedupe. React StrictMode + tab/sport flips can fire the same
// betting/results request twice in quick succession; without a dedupe the
// browser ships both, doubles the wire-time pressure on Cloudflare Tunnel,
// and the second response is thrown away. Map key is method + url + params,
// so concurrent requests with different filters still race.
interface AxiosLikeResponse { data: unknown }
const inFlight = new Map<string, Promise<AxiosLikeResponse>>();

async function dedupedGet<T>(url: string, opts?: { params?: Record<string, unknown>; timeout?: number }): Promise<T> {
  const key = `GET ${url}?${JSON.stringify(opts?.params ?? {})}`;
  const existing = inFlight.get(key);
  if (existing) {
    const res = await existing;
    return res.data as T;
  }
  const promise = apiClient.get(url, opts) as Promise<AxiosLikeResponse>;
  inFlight.set(key, promise);
  try {
    const res = await promise;
    return res.data as T;
  } finally {
    inFlight.delete(key);
  }
}

// Shared DFS lineup query client (all 3 sports). Server contract: Api/CLAUDE.md "DFS lineup query contract".
// `getPage` takes a 0-based page INDEX (the useLineupPage convention) and sends the server's 1-based `page`.
function lineupBase(sport: LineupSport, isShowdown: boolean, contestId: string | number): string {
  const id = encodeURIComponent(String(contestId));
  switch (sport) {
    case 'basketball': return `/api/basketball/BasketballDFSLineup/contests/${id}`;
    case 'baseball': return `/api/baseball/BaseballDFSLineup/contests/${id}`;
    case 'football':
      return isShowdown
        ? `/api/football/FootballShowdownLineup/contests/${id}`
        : `/api/football/FootballDFSLineup/contests/${id}`;
  }
}

const lineupQuery = {
  basePath: lineupBase,

  /** Omits empty/null values; `selectionOnly` drops `search` (selection paths never see it). `sort`/`dir` always sent. */
  toParams(f: LineupFilterState, opts: { selectionOnly: boolean }): Record<string, string> {
    const p: Record<string, string> = {};
    if (f.includeIds.length > 0) p.includeIds = f.includeIds.join(',');
    if (f.excludeIds.length > 0) p.excludeIds = f.excludeIds.join(',');
    const search = f.search.trim();
    if (!opts.selectionOnly && search !== '') p.search = search;
    if (f.projMin != null) p.projMin = String(f.projMin);
    if (f.projMax != null) p.projMax = String(f.projMax);
    if (f.salaryMin != null) p.salaryMin = String(f.salaryMin);
    if (f.salaryMax != null) p.salaryMax = String(f.salaryMax);
    p.sort = f.sort;
    p.dir = f.dir;
    return p;
  },

  async getPage<T>(sport: LineupSport, isShowdown: boolean, contestId: string | number,
    f: LineupFilterState, pageIndex: number, pageSize: number): Promise<LineupPage<T>> {
    const response = await apiClient.get(`${lineupBase(sport, isShowdown, contestId)}/lineups/filtered`, {
      params: { ...lineupQuery.toParams(f, { selectionOnly: false }), page: String(pageIndex + 1), pageSize: String(pageSize) },
      timeout: 30000,
    });
    return response.data;
  },

  async getIds(sport: LineupSport, isShowdown: boolean, contestId: string | number,
    f: LineupFilterState): Promise<{ ids: number[]; totalCount: number }> {
    const response = await apiClient.get(`${lineupBase(sport, isShowdown, contestId)}/lineups/ids`, {
      params: lineupQuery.toParams(f, { selectionOnly: false }),
      timeout: 30000,
    });
    return response.data;
  },

  /**
   * `filter` (selection filters only, no search) makes the server report `filteredOutIds` for stored ids that no longer match.
   * A selection above `LINEUP_BYIDS_MAX` goes out in sequential chunks and comes back merged in request order.
   */
  async getByIds<T>(sport: LineupSport, isShowdown: boolean, contestId: string | number,
    ids: number[], filter?: LineupFilterState): Promise<LineupsByIds<T>> {
    const merged: LineupsByIds<T> = { lineups: [], missingIds: [], filteredOutIds: [] };
    for (let i = 0; i < ids.length; i += LINEUP_BYIDS_MAX) {
      const response = await apiClient.post(`${lineupBase(sport, isShowdown, contestId)}/lineups/byIds`,
        { ids: ids.slice(i, i + LINEUP_BYIDS_MAX) }, {
          params: filter ? lineupQuery.toParams(filter, { selectionOnly: true }) : undefined,
          timeout: 60000,
        });
      const part = response.data as LineupsByIds<T>;
      merged.lineups.push(...(part.lineups ?? []));
      merged.missingIds.push(...(part.missingIds ?? []));
      merged.filteredOutIds.push(...(part.filteredOutIds ?? []));
    }
    return merged;
  },

  async getBands(sport: LineupSport, isShowdown: boolean, contestId: string | number): Promise<LineupBands> {
    return dedupedGet(`${lineupBase(sport, isShowdown, contestId)}/lineups/bands`, { timeout: 30000 });
  },

  async getPlayerOptions(sport: LineupSport, isShowdown: boolean, contestId: string | number): Promise<PlayerOption[]> {
    return dedupedGet(`${lineupBase(sport, isShowdown, contestId)}/player-options`, { timeout: 30000 });
  },
};

export const apiService = {
  lineupQuery,
  // Generic GET for analytics + admin endpoints
  async get(url: string) {
    return apiClient.get(url);
  },

  // Generic write helpers used by admin pages (users, roles).
  async post(url: string, data?: unknown) {
    return apiClient.post(url, data);
  },

  async put(url: string, data?: unknown) {
    return apiClient.put(url, data);
  },

  async delete(url: string) {
    return apiClient.delete(url);
  },

  // Admin-only multipart upload of DraftKings contest CSVs. Standings can only be matched to a
  // contest's date when their history row is uploaded in the SAME request (the server builds the
  // history->contestId lookup fresh per call, not from previously-imported data) — always select
  // the history file together with its standings files. Re-uploading the full growing history is
  // idempotent server-side (no dupes). Long timeout allows for a big-field contest's standings
  // (tens of thousands of entries/player rows) even though the server batches its DB upserts.
  async uploadDkContests(files: File[], username: string): Promise<DkContestUploadResult> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const qs = username ? `?username=${encodeURIComponent(username)}` : '';
    const response = await apiClient.post(`/api/dfs/dkcontest/upload${qs}`, form, { timeout: 300000 });
    return response.data as DkContestUploadResult;
  },

  // Contest performance (Contest History page)
  async getContestPerformanceSummary(
    from?: string,
    to?: string,
    opts?: { groupBy?: HistoryDimension; rollingDays?: number },
  ): Promise<ContestPerformanceSummary> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (opts?.groupBy) params.groupBy = opts.groupBy;
    if (opts?.rollingDays) params.rollingDays = String(opts.rollingDays);
    return dedupedGet('/api/dfs/dkcontest/performance/summary', { params, timeout: 15000 });
  },

  async getContestPerformanceLedger(from?: string, to?: string): Promise<ContestLedgerRow[]> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    return dedupedGet('/api/dfs/dkcontest/performance/ledger', { params, timeout: 15000 });
  },

  // Latest nightly payout-curve fit status (§8) — 404 means no fit has been persisted yet, not an error.
  async getPayoutFitLatest(): Promise<PayoutFitLatest | null> {
    try {
      return await dedupedGet('/api/dfs/dfsevidence/payout-fit/latest', { timeout: 15000 });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      console.warn('Could not load payout fit status:', err);
      return null;
    }
  },

  // Analytics
  async getAnalytics(contestId: string): Promise<LineupAnalyticsViewModel> {
    const response = await apiClient.get(`/api/baseball/BaseballDFSLineup/contests/${contestId}/analytics`);
    return response.data;
  },

  /** Full CSV of the slate's lineups that pass the selection filter (search never applies), in the filter's sort order. */
  /**
   * Full baseball DK CSV. `unresolvedLineups` = the server's `X-Unresolved-Lineups` header (exported lineups with an empty,
   * unresolved slot); null when the header is not readable (e.g. a cross-origin dev setup that does not expose it).
   */
  async exportBaseballLineups(contestId: string, filter?: LineupFilterState): Promise<{ csv: string; unresolvedLineups: number | null }> {
    const response = await apiClient.get(`${lineupBase('baseball', false, contestId)}/lineups/export`, {
      params: filter ? lineupQuery.toParams(filter, { selectionOnly: true }) : undefined,
      responseType: 'text',
      timeout: 60000,
    });
    const raw = response.headers?.['x-unresolved-lineups'];
    const n = raw == null ? NaN : Number(raw);
    return { csv: response.data, unresolvedLineups: Number.isInteger(n) && n >= 0 ? n : null };
  },

  async getContests(sport = 'baseball'): Promise<ContestViewModel[]> {
    const endpoint = sport === 'football'
      ? '/api/football/FootballDFSLineup/contests'
      : '/api/baseball/BaseballDFSLineup/contests';

    const response = await apiClient.get(endpoint);

    // Transform the API response to match our interface (convert lowercase to uppercase)
    return response.data.map((contest: any) => ({
      ContestId: contest.contestId || contest.ContestId,
      GameInfo: contest.gameInfo || contest.GameInfo,
      GameType: contest.gameType || contest.GameType,
      ContestType: contest.contestType || contest.ContestType,
      MaxEntries: contest.maxEntries || contest.MaxEntries,
      EntryFee: contest.entryFee || contest.EntryFee,
      TotalPrizePool: contest.totalPrizePool || contest.TotalPrizePool,
      CurrentEntries: contest.currentEntries || contest.CurrentEntries,
      GameStartTime: contest.gameStartTime || contest.GameStartTime,
      LineupCount: contest.lineupCount || contest.LineupCount,
      Status: contest.status || contest.Status,
      Games: contest.games || contest.Games,
      GameId: contest.gameId || contest.GameId,
      RemainingEntries: contest.remainingEntries || contest.RemainingEntries,
      FieldSize: contest.fieldSize || contest.FieldSize,
      IsFillingUp: contest.isFillingUp || contest.IsFillingUp,
      IsSmallField: contest.isSmallField || contest.IsSmallField,
      IsLargeField: contest.isLargeField || contest.IsLargeField,
    }));
  },

  // Baseball Auto-Select (single + multi entry by contestType; Classic/Showdown by gameType). The selection filter narrows the
  // candidates first (search never reaches it); the response says requested vs returned (never padded). Baseball's selector
  // never rewrites slots, so there is no `restored`.
  async getAutoSelectedBaseballLineups(
    gameId: number,
    contestType: ContestType,
    maxEntries: number,
    gameType: string,
    filter: LineupFilterState,
  ): Promise<SelectionResponse<LineupViewModel | ShowdownLineupViewModel>> {
    const response = await apiClient.get(`/api/baseball/BaseballDFSLineup/game/${gameId}/lineups/auto-select`, {
      params: { ...lineupQuery.toParams(filter, { selectionOnly: true }), contestType, maxEntries, gameType },
      timeout: 60000,
    });
    return response.data;
  },

  // Teams
  async getTeams(): Promise<any[]> {
    const response = await apiClient.get('/api/baseball/BaseballTeam');
    return response.data;
  },

  // Football-specific methods
  async getFootballProjectionSourceHealth(): Promise<FootballProjectionSourceHealth[]> {
    const response = await apiClient.get('/api/football/FootballPlayerProjectedStats/sources/health');
    return response.data;
  },

  /** Full CSV of the slate's lineups that pass the selection filter (search never applies), in the filter's sort order. */
  async exportFootballLineups(contestId: string, isShowdown: boolean, filter?: LineupFilterState): Promise<string> {
    const response = await apiClient.get(`${lineupBase('football', isShowdown, contestId)}/lineups/export`, {
      params: filter ? lineupQuery.toParams(filter, { selectionOnly: true }) : undefined,
      responseType: 'text',
      timeout: 60000,
    });
    return response.data;
  },

  // Football Auto-Select (Classic → FootballDFSLineup, Showdown → FootballShowdownLineup). The selection filter narrows the
  // candidates first (search never reaches it); the response carries requested/returned/restored (never padded).
  async getAutoSelectedFootballLineups(
    gameId: number,
    contestType: ContestType,
    maxEntries: number,
    gameType: string,
    filter: LineupFilterState,
  ): Promise<SelectionResponse<FootballLineupViewModel>> {
    const endpoint = gameType === 'Showdown'
      ? `/api/football/FootballShowdownLineup/game/${gameId}/lineups/auto-select`
      : `/api/football/FootballDFSLineup/game/${gameId}/lineups/auto-select`;
    const response = await apiClient.get(endpoint, {
      params: { ...lineupQuery.toParams(filter, { selectionOnly: true }), contestType, maxEntries, gameType },
      timeout: 60000,
    });
    return response.data;
  },

  // Get game odds by gameInfo (e.g., "DAL@DET") and optional gameDate
  async getFootballGameOdds(gameInfo: string, gameDate?: string): Promise<GameOddsResponse | null> {
    try {
      const params: any = { gameInfo };
      if (gameDate) {
        params.gameDate = gameDate;
      }
      const response = await apiClient.get(`/api/football/FootballGameOdds/game`, { params });
      return response.data;
    } catch (err) {
      console.warn('Could not load game odds:', err);
      return null;
    }
  },

  // Football Multi-Entry Selection (backend strategy; Classic + Showdown via gameType, on FootballDFSLineup only). The selection
  // filter narrows the candidates first (search never reaches it); a Classic lineup the selector swapped out of the filter
  // comes back as its stored version (`restored` counts them, Ruling N). The response says requested vs returned.
  async getMultiEntryFootballLineups(
    gameId: number,
    maxEntries: number,
    gameType: string,
    filter: LineupFilterState,
  ): Promise<SelectionResponse<FootballLineupViewModel>> {
    const response = await apiClient.get(`/api/football/FootballDFSLineup/game/${gameId}/lineups/multi-entry`, {
      params: { ...lineupQuery.toParams(filter, { selectionOnly: true }), maxEntries, gameType },
      timeout: 60000 // 60 second timeout for large lineup processing
    });
    return response.data;
  },

  // Basketball-specific methods
  async getBasketballContests(): Promise<ContestViewModel[]> {
    const response = await apiClient.get('/api/basketball/BasketballDFSLineup/contests');
    return response.data.map((contest: any) => ({
      ContestId: contest.contestId || contest.ContestId,
      GameInfo: contest.gameInfo || contest.GameInfo,
      GameType: contest.gameType || contest.GameType,
      ContestType: contest.contestType || contest.ContestType,
      MaxEntries: contest.maxEntries || contest.MaxEntries,
      EntryFee: contest.entryFee || contest.EntryFee,
      TotalPrizePool: contest.totalPrizePool || contest.TotalPrizePool,
      CurrentEntries: contest.currentEntries || contest.CurrentEntries,
      GameStartTime: contest.gameStartTime || contest.GameStartTime,
      LineupCount: contest.lineupCount || contest.LineupCount,
      Status: contest.status || contest.Status,
      Games: contest.games || contest.Games,
      GameId: contest.gameId || contest.GameId,
      RemainingEntries: contest.remainingEntries || contest.RemainingEntries,
      FieldSize: contest.fieldSize || contest.FieldSize,
      IsFillingUp: contest.isFillingUp || contest.IsFillingUp,
      IsSmallField: contest.isSmallField || contest.IsSmallField,
      IsLargeField: contest.isLargeField || contest.IsLargeField,
    }));
  },

  /** Full CSV of the slate's lineups that pass the selection filter (search never applies), in the filter's sort order. */
  async exportBasketballLineups(contestId: string, filter?: LineupFilterState): Promise<string> {
    const response = await apiClient.get(`/api/basketball/BasketballDFSLineup/contests/${encodeURIComponent(contestId)}/lineups/export`, {
      params: filter ? lineupQuery.toParams(filter, { selectionOnly: true }) : undefined,
      timeout: 60000,
    });
    return response.data;
  },

  // Get game odds for basketball by gameInfo (e.g., "TOR@BOS")
  async getBasketballGameOdds(gameInfo: string, gameDate?: string): Promise<GameOddsResponse | null> {
    try {
      const params: any = { gameInfo };
      if (gameDate) {
        params.gameDate = gameDate;
      }
      const response = await apiClient.get(`/api/basketball/BasketballGameOdds/game`, { params });
      return response.data;
    } catch (err) {
      console.warn('Could not load basketball game odds:', err);
      return null;
    }
  },

  // Basketball Multi-Entry Selection (backend strategy with exposure caps). The selection filter narrows the candidates
  // first (search never reaches it); the response says how many were requested vs returned (never padded).
  async getMultiEntryBasketballLineups(
    gameId: number,
    maxEntries: number,
    gameType: string,
    filter: LineupFilterState,
  ): Promise<SelectionResponse<BasketballLineupViewModel>> {
    const response = await apiClient.get(`/api/basketball/BasketballDFSLineup/game/${gameId}/lineups/multi-entry`, {
      params: { ...lineupQuery.toParams(filter, { selectionOnly: true }), maxEntries, gameType },
      timeout: 60000 // 60 second timeout for large lineup processing
    });
    return response.data;
  },

  // Basketball Auto-Select (single entry = top by enhanced score; multi entry delegates to the multi-entry strategy).
  async getAutoSelectedBasketballLineups(
    gameId: number,
    contestType: ContestType,
    maxEntries: number,
    gameType: string,
    filter: LineupFilterState,
  ): Promise<SelectionResponse<BasketballLineupViewModel>> {
    const response = await apiClient.get(`/api/basketball/BasketballDFSLineup/game/${gameId}/lineups/auto-select`, {
      params: { ...lineupQuery.toParams(filter, { selectionOnly: true }), contestType, maxEntries, gameType },
      timeout: 60000
    });
    return response.data;
  },

  // Basketball Betting Analysis
  async getBettingAnalysis(fromUtc?: string, toUtc?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (fromUtc) params.fromUtc = fromUtc;
    if (toUtc) params.toUtc = toUtc;
    return dedupedGet('/api/basketball/BasketballBetting/today/stored', { timeout: 30000, params });
  },

  // Basketball Betting Available Game Times (for date dropdown)
  async getBettingAvailableGameTimes(): Promise<string[]> {
    return dedupedGet('/api/basketball/BasketballBetting/gametimes', { timeout: 10000 });
  },

  // Basketball Prop Results
  async getPropResultsSummary(date?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return dedupedGet('/api/basketball/BasketballPropResult/summary', { params, timeout: 15000 });
  },

  async getPropResultDates(): Promise<string[]> {
    return dedupedGet('/api/basketball/BasketballPropResult/dates', { timeout: 10000 });
  },

  // Baseball Betting Analysis
  async getBaseballBettingAnalysis(fromUtc?: string, toUtc?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (fromUtc) params.fromUtc = fromUtc;
    if (toUtc) params.toUtc = toUtc;
    return dedupedGet('/api/baseball/BaseballBetting/today/stored', { timeout: 30000, params });
  },

  async getBaseballBettingAvailableGameTimes(): Promise<string[]> {
    return dedupedGet('/api/baseball/BaseballBetting/gametimes', { timeout: 10000 });
  },

  async getBaseballPropResultsSummary(date?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return dedupedGet('/api/baseball/BaseballPropResult/summary', { params, timeout: 15000 });
  },

  async getBaseballPropResultDates(): Promise<string[]> {
    return dedupedGet('/api/baseball/BaseballPropResult/dates', { timeout: 10000 });
  },

  // DFS lineup results (accuracy of snapshotted lineups vs box-score actuals)
  async getBaseballLineupResultDates(): Promise<string[]> {
    return dedupedGet('/api/baseball/BaseballLineupResult/dates', { timeout: 10000 });
  },

  async getBaseballLineupResultsByDate(date: string): Promise<LineupResultRow[]> {
    return dedupedGet(`/api/baseball/BaseballLineupResult/date/${date}`, { timeout: 15000 });
  },

  async getBasketballLineupResultDates(): Promise<string[]> {
    return dedupedGet('/api/basketball/BasketballLineupResult/dates', { timeout: 10000 });
  },

  async getBasketballLineupResultsByDate(date: string): Promise<LineupResultRow[]> {
    return dedupedGet(`/api/basketball/BasketballLineupResult/date/${date}`, { timeout: 15000 });
  },

  async getFootballLineupResultDates(): Promise<string[]> {
    return dedupedGet('/api/football/FootballLineupResult/dates', { timeout: 10000 });
  },

  async getFootballLineupResultsByDate(date: string): Promise<LineupResultRow[]> {
    return dedupedGet(`/api/football/FootballLineupResult/date/${date}`, { timeout: 15000 });
  },

  // My Entries — operator-submitted lineups with their current managed state + late-swaps
  async getBaseballEntries(gameId: number): Promise<EntriesResponse> {
    return dedupedGet(`/api/baseball/BaseballDFSLineup/game/${gameId}/entries`, { timeout: 15000 });
  },

  async markBaseballEntered(gameId: number, body: MarkEnteredRequest): Promise<MarkEnteredResponse> {
    const response = await apiClient.post(`/api/baseball/BaseballDFSLineup/game/${gameId}/mark-entered`, body);
    return response.data;
  },

  async getBasketballEntries(gameId: number): Promise<BasketballEntriesResponse> {
    return dedupedGet(`/api/basketball/BasketballDFSLineup/game/${gameId}/entries`, { timeout: 15000 });
  },

  /** The newest `-p` tick's salary-prior outlier lists for one NBA Classic slate (W8 F2, report only). Always 200; tickUtc null = no tick yet. */
  async getBasketballOutlierFlag(gameId: number): Promise<OutlierFlagResponse> {
    return dedupedGet(`/api/basketball/BasketballSalaryOutlierFlag/slate/${gameId}/latest`, { timeout: 15000 });
  },

  async markBasketballEntered(gameId: number, body: BasketballMarkEnteredRequest): Promise<MarkEnteredResponse> {
    const response = await apiClient.post(`/api/basketball/BasketballDFSLineup/game/${gameId}/mark-entered`, body);
    return response.data;
  },

  async getFootballEntries(gameId: number): Promise<FootballEntriesResponse> {
    return dedupedGet(`/api/football/FootballDFSLineup/game/${gameId}/entries`, { timeout: 15000 });
  },

  async markFootballEntered(gameId: number, body: FootballMarkEnteredRequest): Promise<MarkEnteredResponse> {
    const response = await apiClient.post(`/api/football/FootballDFSLineup/game/${gameId}/mark-entered`, body);
    return response.data;
  },

  async setParlayPlaced(sport: 'baseball' | 'football' | 'basketball', id: number,
    body: { placed: boolean; placedOdds?: number | null; placedStake?: number | null }): Promise<{ id: number; placed: boolean; placedOdds: number | null; placedStake: number | null; placedAtUtc: string | null }> {
    const controller = sport === 'baseball' ? 'BaseballBetting' : sport === 'football' ? 'FootballBetting' : 'BasketballBetting';
    const response = await apiClient.put(`/api/${sport}/${controller}/parlay/${id}/placed`, body);
    return response.data;
  },

  // Entered-entry count per Classic slate — backs the My Entries dropdown badge
  async getDfsEnteredCounts(sport: DfsSport): Promise<DfsEnteredCountsResponse> {
    const controller = sport === 'baseball' ? 'BaseballDFSLineup'
      : sport === 'basketball' ? 'BasketballDFSLineup' : 'FootballDFSLineup';
    return dedupedGet(`/api/${sport}/${controller}/entries/counts`, { timeout: 15000 });
  },

  // Config Audit (for Tuning tab)
  async getBasketballConfigAudit(days = 30, key?: string): Promise<ConfigAuditEntry[]> {
    const params: any = {};
    if (days) params.days = days;
    if (key) params.key = key;
    const response = await apiClient.get('/api/basketball/BasketballProjectionConfig/audit', {
      params,
      timeout: 10000
    });
    return response.data;
  },

  async getBaseballConfigAudit(days = 30, key?: string): Promise<ConfigAuditEntry[]> {
    const params: any = {};
    if (days) params.days = days;
    if (key) params.key = key;
    const response = await apiClient.get('/api/baseball/BaseballProjectionConfig/audit', {
      params,
      timeout: 10000
    });
    return response.data;
  },

  // Football Betting Analysis
  async getFootballBettingAnalysis(fromUtc?: string, toUtc?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (fromUtc) params.fromUtc = fromUtc;
    if (toUtc) params.toUtc = toUtc;
    return dedupedGet('/api/football/FootballBetting/today/stored', { timeout: 30000, params });
  },

  async getFootballBettingGameTimes(): Promise<string[]> {
    return dedupedGet('/api/football/FootballBetting/gametimes', { timeout: 10000 });
  },

  async getFootballPropResultsSummary(date?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return dedupedGet('/api/football/FootballPropResult/summary', { params, timeout: 15000 });
  },

  async getFootballPropResultDates(): Promise<string[]> {
    return dedupedGet('/api/football/FootballPropResult/dates', { timeout: 10000 });
  },

  async getFootballConfigAudit(days = 30, key?: string, source?: string): Promise<ConfigAuditEntry[]> {
    const params: any = {};
    if (days) params.days = days;
    if (key) params.key = key;
    if (source) params.source = source;
    const response = await apiClient.get('/api/football/FootballProjectionConfig/audit', {
      params,
      timeout: 10000
    });
    return response.data;
  },

  // Cross-Sport Dashboard Stats (lightweight counts)
  async getDashboardStats(): Promise<{
    baseball: { propCount: number; gameCount: number; contestCount: number; lineupCount: number };
    basketball: { propCount: number; gameCount: number; contestCount: number; lineupCount: number };
    football: { propCount: number; gameCount: number; contestCount: number; lineupCount: number };
  }> {
    return dedupedGet('/api/crosssport/CrossSportDashboard/stats', { timeout: 10000 });
  },

  // Cross-Sport Analytics
  async getCrossSportPortfolio(days = 30): Promise<any> {
    return dedupedGet('/api/crosssport/CrossSportDashboard/portfolio', { params: { days }, timeout: 30000 });
  },

  async getCrossSportHitRates(days = 30): Promise<any> {
    return dedupedGet('/api/crosssport/CrossSportDashboard/hitrates', { params: { days }, timeout: 30000 });
  },

  async getCrossSportCalibration(days = 30): Promise<any> {
    return dedupedGet('/api/crosssport/CrossSportDashboard/calibration', { params: { days }, timeout: 30000 });
  },

  async getCrossSportSeasonal(months = 6): Promise<any> {
    return dedupedGet('/api/crosssport/CrossSportDashboard/seasonal', { params: { months }, timeout: 30000 });
  },

  async getCrossSportBankroll(): Promise<any> {
    return dedupedGet('/api/crosssport/CrossSportDashboard/bankroll', { timeout: 30000 });
  },

  // Backtest endpoints
  async getBacktestRuns(sport: string, days = 30): Promise<BacktestRun[]> {
    const response = await apiClient.get(`/api/backtest/${sport}/runs`, {
      params: { days }, timeout: 30000
    });
    return response.data;
  },

  async simulateBacktest(sport: string, configOverrides: Record<string, number>): Promise<BacktestSimulationResult> {
    const response = await apiClient.post(`/api/backtest/${sport}/simulate`, configOverrides, {
      timeout: 60000
    });
    return response.data;
  },

  // Correlation endpoints
  async getCorrelations(sport: string): Promise<PropCorrelation[]> {
    const response = await apiClient.get(`/api/correlation/${sport}`, {
      timeout: 15000
    });
    return response.data;
  },

  async getTopCorrelations(sport: string, type?: string, limit = 20): Promise<PropCorrelation[]> {
    const params: any = { limit };
    if (type) params.type = type;
    const response = await apiClient.get(`/api/correlation/${sport}/top`, {
      params, timeout: 15000
    });
    return response.data;
  },

  // Stored Parlays (pre-computed predictions)
  async getBasketballStoredParlays(date?: string): Promise<any[]> {
    const params: any = {};
    if (date) params.date = date;
    const response = await apiClient.get('/api/basketball/BasketballBetting/parlays', { params });
    return response.data;
  },

  async getBaseballStoredParlays(date?: string): Promise<any[]> {
    const params: any = {};
    if (date) params.date = date;
    const response = await apiClient.get('/api/baseball/BaseballBetting/parlays', { params });
    return response.data;
  },

  async getFootballStoredParlays(date?: string): Promise<any[]> {
    const params: any = {};
    if (date) params.date = date;
    const response = await apiClient.get('/api/football/FootballBetting/parlays', { params });
    return response.data;
  },
};

// Config audit entry type
export interface ConfigAuditEntry {
  id: number;
  configKey: string;
  configValue: number;
  previousValue: number | null;
  changedBy: string;
  changeReason: string;
  sampleSize: number | null;
  analysisWindowDays: number | null;
  createdAt: string;
}

// Game odds response type
export interface GameOddsResponse {
  gameInfo: string;
  overUnder: number;
  awayTeam: {
    abbreviation: string;
    name: string;
    spread: number;
    impliedScore: number;
    moneyline: number;
  };
  homeTeam: {
    abbreviation: string;
    name: string;
    spread: number;
    impliedScore: number;
    moneyline: number;
  };
}
